import { Button, TableColumnsType, Tooltip, message } from 'antd';
import ScrollTable, { ScrollTableProps } from 'components/ScrollTable';
import StatusSwitch from 'components/StatusSwitch';
import StatusButton from 'components/StatusButton';
import { useApplyScope2storeScopes, useGetTaskName } from './TaskForm/share';
import { permissionNameIdMap } from 'hooks/business/useNewPermisson';
import { deleteTask, updateInsiteEnable } from 'services/api/remind';
import { DefaultTag } from 'views/Aide/Scenario/TaskTable';
import { useOrgConfigStateValue } from 'hooks/business/useOrgConfig';
import PopoverPreview from 'components/StoreScopes/PopoverPreview';

type RT = Remind.BasicConfig;

export interface RemindTableProps extends ScrollTableProps<RT> {
  onCheckDetail: (record: RT) => void;
  onUpdated: () => any;
}

function RemindTable(props: RemindTableProps) {
  const { onCheckDetail, onUpdated, ...otherProps } = props;

  const { whiteListConfig: { AIDE_REMOVE_TASK } = {} } = useOrgConfigStateValue();

  const { t, getTaskName } = useGetTaskName();

  const { applyScope2storeScopes } = useApplyScope2storeScopes({
    permId: permissionNameIdMap['重要提醒'],
  });

  const columns: TableColumnsType<RT> = [
    {
      key: 'taskName',
      title: AIDE_REMOVE_TASK ? t('marketing.策略名称') : t('marketing.任务名称'),
      width: 150,
      render: (_, record) => {
        const { isDefault, taskName } = record;

        if (!isDefault) {
          return taskName;
        }

        return (
          <div>
            {getTaskName(record)}
            <Tooltip
              title={t(
                'aide.新授权店铺会自动添加至默认任务中，当店铺从默认任务或其他任务中移除时，不会再自动加入默认任务中',
              )}
            >
              <DefaultTag color="blue">{t('marketing.默认')}</DefaultTag>
            </Tooltip>
          </div>
        );
      },
    },
    {
      key: 'applyScope',
      dataIndex: 'applyScope',
      title: t('aide.应用店铺'),
      width: 120,
      render: (applyScope: RT['applyScope']) => {
        if (applyScope.scope === 'ALL') {
          return t('department.全部店铺');
        }

        const storeScopes = applyScope2storeScopes(applyScope);

        return <PopoverPreview value={storeScopes} />;
      },
    },
    {
      key: 'inSiteRemindEnable',
      dataIndex: 'inSiteRemindEnable',
      title: AIDE_REMOVE_TASK ? t('aide.策略开关') : t('aide.任务开关'),
      width: 100,
      render: (_, { taskId, inSiteRemindEnable }) => {
        return (
          <StatusSwitch
            checkedChildren={t('aide.开')}
            unCheckedChildren={t('aide.关')}
            checked={inSiteRemindEnable}
            onChange={(inSiteRemindEnable) => {
              return updateInsiteEnable({
                taskId,
                inSiteRemindEnable,
              })
                .then(() => {
                  message.success(t('operation.saveSucceed'));

                  return onUpdated();
                })
                .catch(() => {
                  message.error(t('operation.saveFailed'));
                });
            }}
          />
        );
      },
    },
    {
      key: 'operation',
      title: t('操作'),
      width: 120,
      render: (_, record) => {
        const { taskId, isDefault } = record;

        return (
          <div>
            <Button type="link" onClick={() => onCheckDetail(record)}>
              {t('tableColumn.查看详情')}
            </Button>
            {!isDefault && (
              <StatusButton
                type="link"
                popConfirmProps={{
                  placement: 'topRight',
                  title: AIDE_REMOVE_TASK
                    ? t('marketing.您确定删除该策略吗？')
                    : t('marketing.你确定要删除当前任务吗'),
                  okText: t('marketing.删除'),
                  cancelText: t('marketing.取消'),
                }}
                onClick={() => {
                  return deleteTask({ taskId })
                    .then(() => {
                      message.success(t('phrase.删除成功'));

                      return onUpdated();
                    })
                    .catch(() => {
                      message.error(t('phrase.删除失败，请稍后再试'));
                    });
                }}
              >
                {t('marketing.删除')}
              </StatusButton>
            )}
          </div>
        );
      },
    },
  ];

  return <ScrollTable rowKey="taskId" columns={columns} {...otherProps} />;
}

export default RemindTable;
